import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { Button } from 'antd';
import streetViewIcon from '../../images/street-view.png';
import streetViewRingIcon from '../../images/street-view-ring.png';
import streetViewPersonIcon from '../../images/street-view-person.png';
import './index.less';

// A press that travels further than this is a drag; anything shorter is still a tap on the toggle.
const DRAG_THRESHOLD = 6;
// Snap onto a 360 point whose centre is within this many pixels of the figure's feet — about a
// fingertip. The points themselves are drawn 3–5px across, far too small to hit exactly.
const SNAP_RADIUS = 40;
// On touch the figure rides this far above the fingertip, so the finger never covers the spot it is
// about to land on. A mouse cursor hides nothing, so there it is held by the feet.
const TOUCH_LIFT = 64;
// Keep in step with the transitions in index.less.
const RETURN_MS = 250;
const LAND_MS = 180;
// Where the person's feet sit in the button artwork (512px square): centred, 405px down, and how
// much of its height the person takes up.
const ICON_FEET_Y = 405 / 512;
const ICON_PERSON_HEIGHT = 397 / 512;
// Height of the lifted figure, as drawn by index.less.
const FIGURE_HEIGHT = 52;

// Preload the figure so it is on screen the moment a drag starts, not a network round trip later.
new Image().src = streetViewPersonIcon;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

// The 360 toggle, and the Pegman of Google Maps: a tap shows or hides the floor's 360 points, and
// dragging the person out of the icon and dropping it onto a point opens that point's 360 view.
//
// The press is captured by the button for its whole life, so the map underneath never sees the drag
// and cannot pan along with it — which is also what keeps the projected positions of the points valid
// until the drop.
const StreetViewPegman = ({ active, mapRef, nodes, style, iconSize = 30, onToggle, onDragStart, onDragEnd, onDrop, onMiss }) => {
	const buttonRef = useRef(null);
	const iconRef = useRef(null);
	// The press in progress: { pointerId, startX, startY, lift, dragging, lastX, tilt }.
	const pressRef = useRef(null);
	// The browser still fires `click` on the button when a drag ends, which must not toggle. Not every
	// drag is followed by one (a touch that travelled, a drag put back with Escape), so the flag is
	// also dropped shortly after, or it would swallow the next keyboard press.
	const suppressClickRef = useRef(false);
	const suppressClickTimerRef = useRef(null);
	const settleTimerRef = useRef(null);
	const phaseTimerRef = useRef(null);
	const mountedRef = useRef(true);
	// What is drawn over the page: { phase: 'dragging' | 'landing' | 'returning', x, y, tilt, target }.
	// `x`/`y` are the figure's feet in viewport pixels; `target` is the point it would land on.
	const [drag, setDrag] = useState(null);

	useEffect(() => () => {
		mountedRef.current = false;
		clearTimeout(settleTimerRef.current);
		clearTimeout(phaseTimerRef.current);
		clearTimeout(suppressClickTimerRef.current);
	}, []);

	// Where a drop at (x, y) would land. Only a drop onto the map itself counts: over the bottom
	// sheet, the search field or these buttons the figure just goes home, as if the drag never began.
	const findLanding = (x, y) => {
		const map = mapRef.current?.map;
		if (!map) return { overMap: false, target: null };

		const container = map.getContainer();
		const under = document.elementFromPoint(x, y);
		if (!under || !container.contains(under)) return { overMap: false, target: null };

		const rect = container.getBoundingClientRect();
		let target = null;
		let nearest = SNAP_RADIUS;
		(nodes || []).forEach((node) => {
			const point = map.project([node.longitude, node.latitude]);
			const pointX = rect.left + point.x;
			const pointY = rect.top + point.y;
			const distance = Math.hypot(pointX - x, pointY - y);
			if (distance <= nearest) {
				nearest = distance;
				target = { node, x: pointX, y: pointY };
			}
		});

		return { overMap: true, target };
	};

	// Points usually arrive after the drag has started (they are only fetched once it does), so a
	// figure already held still over one has to pick it up without waiting for the pointer to move.
	useEffect(() => {
		if (drag?.phase !== 'dragging') return;
		const { target } = findLanding(drag.x, drag.y);
		if (target?.node._id !== drag.target?.node._id) setDrag({ ...drag, target });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [nodes]);

	const releaseClickSuppression = () => {
		clearTimeout(suppressClickTimerRef.current);
		suppressClickTimerRef.current = setTimeout(() => { suppressClickRef.current = false; }, 400);
	};

	const returnHome = () => {
		clearTimeout(settleTimerRef.current);
		releaseClickSuppression();
		const icon = iconRef.current?.getBoundingClientRect();
		setDrag((current) => ({
			...current,
			phase: 'returning',
			x: icon ? icon.left + icon.width / 2 : current?.x,
			y: icon ? icon.top + icon.height * ICON_FEET_Y : current?.y,
			tilt: 0,
			target: null
		}));
		onDragEnd();
		phaseTimerRef.current = setTimeout(() => setDrag(null), RETURN_MS);
	};

	const land = (target) => {
		clearTimeout(settleTimerRef.current);
		releaseClickSuppression();
		setDrag({ phase: 'landing', x: target.x, y: target.y, tilt: 0, target });
		phaseTimerRef.current = setTimeout(() => {
			onDrop(target.node);
			// Opening the 360 view normally unmounts the map along with this button.
			if (!mountedRef.current) return;
			setDrag(null);
			onDragEnd();
		}, LAND_MS);
	};

	const handlePointerDown = (e) => {
		if (pressRef.current || drag) return;
		if (e.pointerType === 'mouse' && e.button !== 0) return;

		pressRef.current = {
			pointerId: e.pointerId,
			startX: e.clientX,
			startY: e.clientY,
			lift: e.pointerType === 'mouse' ? 0 : TOUCH_LIFT,
			dragging: false,
			lastX: e.clientX,
			tilt: 0
		};
		clearTimeout(suppressClickTimerRef.current);
		suppressClickRef.current = false;
		e.currentTarget.setPointerCapture(e.pointerId);
	};

	const handlePointerMove = (e) => {
		const press = pressRef.current;
		if (!press || press.pointerId !== e.pointerId) return;

		if (!press.dragging) {
			if (Math.hypot(e.clientX - press.startX, e.clientY - press.startY) < DRAG_THRESHOLD) return;
			press.dragging = true;
			suppressClickRef.current = true;
			onDragStart();
		}

		// Lean into the direction of travel, and stand upright again once the pointer rests.
		press.tilt = clamp(press.tilt * 0.5 + (e.clientX - press.lastX) * 1.2, -24, 24);
		press.lastX = e.clientX;
		clearTimeout(settleTimerRef.current);
		settleTimerRef.current = setTimeout(() => {
			if (pressRef.current !== press) return;
			press.tilt = 0;
			setDrag(current => current?.phase === 'dragging' ? { ...current, tilt: 0 } : current);
		}, 120);

		const x = e.clientX;
		const y = e.clientY - press.lift;
		setDrag({ phase: 'dragging', x, y, tilt: press.tilt, target: findLanding(x, y).target });
	};

	const handlePointerUp = (e) => {
		const press = pressRef.current;
		if (!press || press.pointerId !== e.pointerId) return;
		pressRef.current = null;
		// A tap: the `click` that follows does the toggling.
		if (!press.dragging) return;

		const { overMap, target } = findLanding(e.clientX, e.clientY - press.lift);
		if (target) {
			land(target);
			return;
		}

		returnHome();
		if (overMap) onMiss();
	};

	// The browser took the pointer away (a system gesture, an incoming call) or capture was lost
	// some other way. Also raised after every ordinary release, by which time the press is gone.
	const handlePointerAbort = (e) => {
		const press = pressRef.current;
		if (!press || press.pointerId !== e.pointerId) return;
		pressRef.current = null;
		if (press.dragging) returnHome();
	};

	// Escape puts the figure back, as it does for a drag on Google Maps.
	useEffect(() => {
		if (drag?.phase !== 'dragging') return undefined;

		const handleKeyDown = (e) => {
			if (e.key !== 'Escape' || !pressRef.current) return;
			const { pointerId } = pressRef.current;
			pressRef.current = null;
			if (buttonRef.current?.hasPointerCapture(pointerId)) buttonRef.current.releasePointerCapture(pointerId);
			returnHome();
		};

		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [drag?.phase]);

	const handleClick = () => {
		if (suppressClickRef.current) {
			suppressClickRef.current = false;
			return;
		}
		onToggle();
	};

	const iconStyle = { filter: active ? 'brightness(0) invert(1)' : 'none' };

	return (
		<>
			<Button
				ref={buttonRef}
				size="large"
				type={active ? 'primary' : 'default'}
				className={`street-view-pegman-button${drag ? ' is-empty' : ''}${drag?.phase === 'dragging' ? ' is-dragging' : ''}`}
				onClick={handleClick}
				onPointerDown={handlePointerDown}
				onPointerMove={handlePointerMove}
				onPointerUp={handlePointerUp}
				onPointerCancel={handlePointerAbort}
				onLostPointerCapture={handlePointerAbort}
				// Long-pressing the artwork would otherwise offer to save or drag the image.
				onDragStart={e => e.preventDefault()}
				onContextMenu={e => e.preventDefault()}
				title="Tap to show 360° views, or drag onto the map"
				aria-label="360° view"
				aria-pressed={active}
				icon={
					<span
						ref={iconRef}
						className="street-view-pegman-icon"
						style={{ width: iconSize, height: iconSize }}
					>
						{/* Both drawn at once so that lifting the person out swaps nothing that has to load. */}
						<img
							src={streetViewRingIcon}
							alt=""
							draggable={false}
							style={iconStyle}
						/>
						<img
							src={streetViewIcon}
							alt=""
							draggable={false}
							style={iconStyle}
							className="street-view-pegman-icon-full"
						/>
					</span>
				}
				style={style}
			/>
			{
				drag && ReactDOM.createPortal(
					<div
						className={`street-view-pegman-layer is-${drag.phase}`}
						// Coming home, the figure shrinks to exactly the person in the button's icon.
						style={{ '--pegman-home-scale': iconSize * ICON_PERSON_HEIGHT / FIGURE_HEIGHT }}
						aria-hidden="true"
					>
						{
							drag.target &&
							<div
								key={drag.target.node._id}
								className="street-view-pegman-target"
								style={{ transform: `translate(${drag.target.x}px, ${drag.target.y}px)` }}
							/>
						}
						<div
							className="street-view-pegman"
							style={{ transform: `translate(${drag.x}px, ${drag.y}px)` }}
						>
							<div className="street-view-pegman-shadow" />
							<div
								className="street-view-pegman-lean"
								style={{ transform: `rotate(${drag.tilt}deg)` }}
							>
								<img
									src={streetViewPersonIcon}
									alt=""
									draggable={false}
									className="street-view-pegman-figure"
								/>
							</div>
						</div>
					</div>,
					document.body
				)
			}
		</>
	);
};

export default StreetViewPegman;
